import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance, getActiveAccount, SCOPES } from "./msalConfig.js";

/** Phase 15 D-AUTH-GATE-01 — top-level Suspense boundary. Children render
 *  iff acquireTokenSilent succeeds. Failure triggers loginRedirect, which
 *  preserves the deep link via sessionStorage (AuthCallback reads it).
 *
 *  All routes inside this gate are auth-gated by construction (no anonymous
 *  surface in the operator console — matches AUTH-04 + Phase 14 D-PROV-01
 *  pre-provision required). The /auth/callback route lives OUTSIDE this
 *  gate (handled at App.tsx level), since it needs to consume the redirect
 *  before any acquireTokenSilent can succeed.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<"checking" | "ready" | "redirecting">("checking");
  const location = useLocation();

  useEffect(() => {
    const account = getActiveAccount();
    if (!account) {
      // No account — kick off login redirect; preserve deep link.
      sessionStorage.setItem("ardept:returnTo", location.pathname + location.search);
      setStatus("redirecting");
      msalInstance.loginRedirect({ scopes: SCOPES }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("operator-console loginRedirect failed:", err);
      });
      return;
    }

    // Account present — try silent refresh to ensure the bearer is fresh.
    msalInstance.acquireTokenSilent({ account, scopes: SCOPES })
      .then(() => setStatus("ready"))
      .catch((err) => {
        if (err instanceof InteractionRequiredAuthError) {
          sessionStorage.setItem("ardept:returnTo", location.pathname + location.search);
          setStatus("redirecting");
          msalInstance.loginRedirect({ scopes: SCOPES });
        } else {
          // eslint-disable-next-line no-console
          console.error("operator-console acquireTokenSilent failed:", err);
          setStatus("redirecting");
          sessionStorage.setItem("ardept:returnTo", location.pathname + location.search);
          msalInstance.loginRedirect({ scopes: SCOPES });
        }
      });
    // Intentionally only run on mount — re-running on route changes would
    // double-fire silent refresh; the fetcher handles per-request retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status !== "ready") return <div className="auth-loading">Checking your session...</div>;
  return <>{children}</>;
}
